import React, { useState } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Select,
  useDisclosure,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
  Spinner,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  SimpleGrid,
} from '@chakra-ui/react';
import { FiPlus, FiMoreVertical, FiEdit2, FiTrash2, FiUser, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { useUserContext } from '../../context/UserContext';
import {
  useGetBranches,
  useCreateBranchUser,
  useGetBranchUsers,
  useGetAllUsers,
  useTransferUser,
  useUpdateUserStatus,
  useDeleteUser,
  useGetUserDetails,
} from '../../services/companyService';
import { formatRoleName } from '../../utils/roleFormatter';

const UserManagement = () => {
  const { isAdmin, canManageUsers, userRole } = useUserContext();
  const toast = useToast();
  
  // States
  const [selectedBranch, setSelectedBranch] = useState('all'); // Default to show all users
  const [filters, setFilters] = useState({
    branch_id: 'all',
    role: 'all',
    status: 'all'
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    branch_id: ''
  });
  const [editingUser, setEditingUser] = useState(null);
  const [transferUser, setTransferUser] = useState(null);
  const [selectedUserForAction, setSelectedUserForAction] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Modals
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isTransferOpen, onOpen: onTransferOpen, onClose: onTransferClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  
  // API hooks
  const { branches: branchesData, branchesIsLoading } = useGetBranches();
  const { allUsers, allUsersIsLoading, refetchAllUsers } = useGetAllUsers(filters);
  const { branchUsers, branchUsersIsLoading, refetchBranchUsers } = useGetBranchUsers(selectedBranch === 'all' ? null : selectedBranch);
  
  // Helper function to get branches array from different possible response structures
  const getBranchesArray = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (data.data && Array.isArray(data.data)) return data.data;
    if (data.branches && Array.isArray(data.branches)) return data.branches;
    return [];
  };

  const branchesArray = getBranchesArray(branchesData);

  // Helper function to get current users data
  const getCurrentUsersData = () => {
    if (selectedBranch === 'all') {
      return allUsers?.data || allUsers || [];
    }
    return branchUsers?.data || branchUsers || [];
  };

  const getCurrentUsersLoading = () => {
    if (selectedBranch === 'all') {
      return allUsersIsLoading;
    }
    return branchUsersIsLoading;
  };

  const getCurrentUsersRefetch = () => {
    if (selectedBranch === 'all') {
      return refetchAllUsers;
    }
    return refetchBranchUsers;
  };

  const currentUsersData = getCurrentUsersData();
  const currentUsersLoading = getCurrentUsersLoading();
  const { createBranchUserPayload, createBranchUserIsLoading: isCreating } = useCreateBranchUser(() => {
    // Success callback - close modal, reset form, and refresh list
    onCreateClose();
    setFormData({ name: '', email: '', password: '', role: 'user', branch_id: '' });
    
    // Refetch the users list to show the newly created user
    if (selectedBranch === 'all') {
      refetchAllUsers();
    } else {
      refetchBranchUsers();
    }
  });
  const { transferUserPayload: transferUserMutation, transferUserIsLoading: isTransferring } = useTransferUser(() => {
    // Success callback - close modal and refresh list
    onTransferClose();
    setTransferUser(null);
    
    // Refetch the users list
    if (selectedBranch === 'all') {
      refetchAllUsers();
    } else {
      refetchBranchUsers();
    }
  });
  const { mutate: updateUserStatusMutation, isPending: isUpdatingStatus } = useUpdateUserStatus();
  const { mutate: deleteUserMutation, isPending: isDeletingUser } = useDeleteUser();
  const { userDetails, userDetailsIsLoading, refetchUserDetails } = useGetUserDetails(selectedUserForAction?.user_id);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      branch_id: 'all',
      role: 'all',
      status: 'all'
    });
  };

  // Handle user status update
  const handleUpdateUserStatus = (userId, newStatus) => {
    updateUserStatusMutation({
      user_id: userId,
      status: newStatus
    }, {
      onSuccess: () => {
        toast({
          title: 'Success',
          description: `User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
          variant: 'solid',
        });
        // Refetch the users list
        if (selectedBranch === 'all') {
          refetchAllUsers();
        } else {
          refetchBranchUsers();
        }
      },
      onError: (error) => {
        toast({
          title: 'Error',
          description: error?.message || 'Failed to update user status',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top',
          variant: 'solid',
        });
      }
    });
  };

  // Handle user deletion
  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    onDeleteOpen();
  };

  const confirmDeleteUser = () => {
    if (!userToDelete) return;
    
    deleteUserMutation(userToDelete.user_id, {
      onSuccess: () => {
        toast({
          title: 'Success',
          description: 'User deleted successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
          variant: 'solid',
        });
        onDeleteClose();
        setUserToDelete(null);
        // Refetch the users list
        if (selectedBranch === 'all') {
          refetchAllUsers();
        } else {
          refetchBranchUsers();
        }
      },
      onError: (error) => {
        toast({
          title: 'Error',
          description: error?.message || 'Failed to delete user',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top',
          variant: 'solid',
        });
      }
    });
  };

  // Handle view user details
  const handleViewUserDetails = (user) => {
    setSelectedUserForAction(user);
    setShowUserDetails(true);
  };
  
  // Handle form changes
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  // Handle create user
  const handleCreateUser = () => {
    if (!formData.name || !formData.email || !formData.password || !formData.branch_id) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
        variant: 'solid',
      });
      return;
    }
    
    // Prepare the payload in the correct format expected by the API
    const payload = {
      name: formData.name,
      email: formData.email,
      password: formData.password,
      role: formData.role
    };
    
    createBranchUserPayload({
      branchId: formData.branch_id,
      payload: payload
    });
  };
  
  // Handle transfer user
  const handleTransferUser = (newBranchId) => {
    if (!transferUser || !newBranchId) return;
    
    transferUserMutation({
      userId: transferUser.user_id,
      payload: {
        new_branch_id: newBranchId
      }
    });
  };
  
  // Debug logging (can be removed after testing)
  // console.log('UserManagement Debug:', {
  //   isAdmin,
  //   canManageUsers,
  //   userRole: localStorage.getItem('userRole')
  // });

  // Check if user role is still loading (userRole starts as null)
  if (userRole === null) {
    return (
      <Box p={6}>
        <Flex justify="center" align="center" minH="200px">
          <Spinner size="lg" />
        </Flex>
      </Box>
    );
  }

  // Check permissions
  if (!isAdmin || !canManageUsers) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You don't have permission to access User Management.
          </AlertDescription>
        </Alert>
      </Box>
    );
  }
  
  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <Heading size="lg" color="gray.900" fontWeight="600">
            User Management
          </Heading>
          <Text color="gray.600" fontSize="md">
            Manage branch users and permissions
          </Text>
        </Box>
        
                 {/* Filters Section */}
         <Box>
           <VStack spacing={4} align="stretch">
             <HStack justify="space-between" align="center">
               <Text fontSize="lg" fontWeight="600" color="gray.700">
                 Filters
               </Text>
               <Button
                 size="sm"
                 variant="outline"
                 onClick={clearFilters}
                 colorScheme="gray"
               >
                 Clear All
               </Button>
             </HStack>
             
             <HStack spacing={4} align="center" flexWrap="wrap">
               <VStack align="start" spacing={1}>
                 <Text fontSize="sm" color="gray.600" fontWeight="500">Branch:</Text>
                 <Select
                   value={filters.branch_id}
                   onChange={(e) => handleFilterChange('branch_id', e.target.value)}
                   size="md"
                   w="180px"
                   borderRadius="8px"
                 >
                   <option value="all">All Branches</option>
                   {branchesArray?.map((branch) => (
                     <option key={branch.branch_id} value={branch.branch_id}>
                       {branch.name}
                     </option>
                   ))}
                 </Select>
               </VStack>

               <VStack align="start" spacing={1}>
                 <Text fontSize="sm" color="gray.600" fontWeight="500">Role:</Text>
                 <Select
                   value={filters.role}
                   onChange={(e) => handleFilterChange('role', e.target.value)}
                   size="md"
                   w="180px"
                   borderRadius="8px"
                 >
                   <option value="all">All Roles</option>
                   <option value="company_admin">Company Admin</option>
                   <option value="branch_user">Branch User</option>
                 </Select>
               </VStack>

               <VStack align="start" spacing={1}>
                 <Text fontSize="sm" color="gray.600" fontWeight="500">Status:</Text>
                 <Select
                   value={filters.status}
                   onChange={(e) => handleFilterChange('status', e.target.value)}
                   size="md"
                   w="180px"
                   borderRadius="8px"
                 >
                   <option value="all">All Status</option>
                   <option value="active">Active</option>
                   <option value="inactive">Inactive</option>
                 </Select>
               </VStack>
             </HStack>
           </VStack>
         </Box>
        
        {/* Actions */}
        <HStack justify="space-between">
          <Text fontSize="lg" fontWeight="600">
            Branch Users
          </Text>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            onClick={onCreateOpen}
          >
            Add User
          </Button>
        </HStack>
        
        {/* Users Table */}
        <Box bg="white" borderRadius="lg" shadow="sm" overflow="hidden">
          {currentUsersLoading ? (
            <Flex justify="center" p={8}>
              <Spinner size="lg" />
            </Flex>
          ) : (
            <Table variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Role</Th>
                  <Th>Status</Th>
                  <Th>Branch</Th>
                  <Th>Created At</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
                              <Tbody>
                  {currentUsersData?.map((user) => (
                    <Tr key={user.user_id}>
                      <Td>
                        <HStack>
                          <FiUser />
                          <Text>{user.name}</Text>
                        </HStack>
                      </Td>
                      <Td>{user.email}</Td>
                      <Td>
                        <Badge
                          colorScheme={user.role === 'company_admin' ? 'red' : user.role === 'branch_user' ? 'blue' : 'gray'}
                          variant="subtle"
                        >
                          {formatRoleName(user.role)}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={user.status === 'active' ? 'green' : 'gray'}
                          variant="subtle"
                        >
                          {user.status}
                        </Badge>
                      </Td>
                      <Td>
                        <Text fontSize="sm" color="gray.600">
                          {user.branch_name || 'N/A'}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm" color="gray.600">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                        </Text>
                      </Td>
                      <Td>
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<FiMoreVertical />}
                            variant="ghost"
                            size="sm"
                          />
                          <MenuList>
                            <MenuItem
                              icon={<FiUser />}
                              onClick={() => handleViewUserDetails(user)}
                            >
                              View Details
                            </MenuItem>
                            <MenuItem
                              icon={<FiEdit2 />}
                              onClick={() => {
                                setEditingUser(user);
                                onCreateOpen();
                              }}
                            >
                              Edit
                            </MenuItem>
                            <MenuItem
                              icon={<FiTrash2 />}
                              onClick={() => {
                                setTransferUser(user);
                                onTransferOpen();
                              }}
                            >
                              Transfer
                            </MenuItem>
                            <MenuItem
                              icon={user.status === 'active' ? <FiXCircle /> : <FiCheckCircle />}
                              onClick={() => handleUpdateUserStatus(user.user_id, user.status === 'active' ? 'inactive' : 'active')}
                              color={user.status === 'active' ? 'red.500' : 'green.500'}
                            >
                              {user.status === 'active' ? 'Deactivate' : 'Activate'}
                            </MenuItem>
                            <MenuItem
                              icon={<FiTrash2 />}
                              onClick={() => handleDeleteUser(user)}
                              color="red.500"
                            >
                              Delete
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
            </Table>
          )}
          
          {currentUsersData?.length === 0 && !currentUsersLoading && (
            <Box p={8} textAlign="center">
              <Text color="gray.500">
                {selectedBranch === 'all' ? 'No users found' : 'No users found in this branch'}
              </Text>
            </Box>
          )}
        </Box>
      </VStack>
      
      {/* Create/Edit User Modal */}
      <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingUser ? 'Edit User' : 'Add New User'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  placeholder="Enter user name"
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  placeholder="Enter email address"
                />
              </FormControl>
              
              {!editingUser && (
                <FormControl>
                  <FormLabel>Password</FormLabel>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleFormChange('password', e.target.value)}
                    placeholder="Enter password"
                  />
                </FormControl>
              )}
              
              <FormControl>
                <FormLabel>Role</FormLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => handleFormChange('role', e.target.value)}
                >
                  <option value="user">Regular User</option>
                  <option value="admin">Company Admin</option>
                  <option value="HR_MANAGER">HR Manager</option>
                  <option value="ACCOUNTANT">Accountant</option>
                  <option value="PAYROLL_VIEWER">Payroll Viewer</option>
                  <option value="PAYROLL_ADMIN">Payroll Admin</option>
                </Select>
              </FormControl>
              
                             <FormControl>
                 <FormLabel>Branch</FormLabel>
                                   <Select
                    value={formData.branch_id}
                    onChange={(e) => handleFormChange('branch_id', e.target.value)}
                    placeholder={branchesIsLoading ? "Loading branches..." : "Select branch"}
                    isDisabled={branchesIsLoading}
                  >
                    {branchesArray?.map((branch) => (
                      <option key={branch.branch_id} value={branch.branch_id}>
                        {branch.name}
                      </option>
                    ))}
                  </Select>
               </FormControl>

              
              <HStack spacing={4} w="full">
                <Button
                  colorScheme="blue"
                  onClick={handleCreateUser}
                  isLoading={isCreating}
                  w="full"
                >
                  {editingUser ? 'Update User' : 'Create User'}
                </Button>
                <Button onClick={onCreateClose} w="full">
                  Cancel
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
      
      {/* Transfer User Modal */}
      <Modal isOpen={isTransferOpen} onClose={onTransferClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Transfer User</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <Text>
                Transfer <strong>{transferUser?.name}</strong> to a different branch:
              </Text>
              
                             <FormControl>
                 <FormLabel>New Branch</FormLabel>
                                   <Select
                    onChange={(e) => handleTransferUser(e.target.value)}
                    placeholder={branchesIsLoading ? "Loading branches..." : "Select new branch"}
                    isDisabled={branchesIsLoading}
                  >
                    {branchesArray?.map((branch) => (
                      <option key={branch.branch_id} value={branch.branch_id}>
                        {branch.name}
                      </option>
                    ))}
                  </Select>
               </FormControl>
              
              <HStack spacing={4} w="full">
                <Button
                  colorScheme="blue"
                  isLoading={isTransferring}
                  w="full"
                  isDisabled={!transferUser}
                >
                  Transfer User
                </Button>
                <Button onClick={onTransferClose} w="full">
                  Cancel
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* User Details Modal */}
      <Modal isOpen={showUserDetails} onClose={() => setShowUserDetails(false)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>User Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {userDetailsIsLoading ? (
              <Flex justify="center" p={8}>
                <Spinner size="lg" />
              </Flex>
            ) : userDetails ? (
              <VStack spacing={4} align="stretch">
                <SimpleGrid columns={2} spacing={4}>
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color="gray.600">Name</Text>
                    <Text>{userDetails.name}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color="gray.600">Email</Text>
                    <Text>{userDetails.email}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color="gray.600">Role</Text>
                    <Badge
                      colorScheme={userDetails.role === 'company_admin' ? 'red' : 'blue'}
                      variant="subtle"
                    >
                      {formatRoleName(userDetails.role)}
                    </Badge>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color="gray.600">Status</Text>
                    <Badge
                      colorScheme={userDetails.status === 'active' ? 'green' : 'gray'}
                      variant="subtle"
                    >
                      {userDetails.status}
                    </Badge>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color="gray.600">Branch</Text>
                    <Text>{userDetails.branch_name || 'N/A'}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color="gray.600">Created At</Text>
                    <Text>{userDetails.created_at ? new Date(userDetails.created_at).toLocaleDateString() : 'N/A'}</Text>
                  </Box>
                </SimpleGrid>
                
                {userDetails.last_login && (
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color="gray.600">Last Login</Text>
                    <Text>{new Date(userDetails.last_login).toLocaleString()}</Text>
                  </Box>
                )}
              </VStack>
            ) : (
              <Text color="gray.500">No user details available</Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm User Deletion</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <Alert status="warning">
                <AlertIcon />
                <VStack align="start" spacing={1}>
                  <AlertTitle>Warning!</AlertTitle>
                  <AlertDescription>
                    You are about to delete the user <strong>{userToDelete?.name}</strong>. 
                    This action cannot be undone and will permanently remove the user from the system.
                  </AlertDescription>
                </VStack>
              </Alert>
              
              <Box>
                <Text fontSize="sm" fontWeight="600" color="gray.700" mb={2}>
                  User Details:
                </Text>
                <VStack spacing={2} align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">Name:</Text>
                    <Text fontSize="sm" fontWeight="500">{userToDelete?.name}</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">Email:</Text>
                    <Text fontSize="sm" fontWeight="500">{userToDelete?.email}</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">Role:</Text>
                    <Badge
                      colorScheme={userToDelete?.role === 'company_admin' ? 'red' : 'blue'}
                      variant="subtle"
                      size="sm"
                    >
                      {formatRoleName(userToDelete?.role)}
                    </Badge>
                  </HStack>
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={confirmDeleteUser}
              isLoading={isDeletingUser}
              loadingText="Deleting..."
            >
              Delete User
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default UserManagement; 